// Configuration API
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_EP_URI;

export interface GitlabGroupMember {
  user_id: number;
  login: string;
  gitlab_username: string | null;
  lastname: string;
  firstname: string;
}

export interface GitlabGroupStatus {
  id: number;
  name: string;
  generic_name: string;
  td_group: string;
  id_group: number;
  members: GitlabGroupMember[];
  gitlab_project_id: number | null;
  gitlab_web_url: string | null;
  gitlab_import_status: string | null;
  gitlab_deploy_key_present: boolean;
  gitlab_deploy_key_expires_at: string | null;
  gitlab_members_access_level: string | null;
  gitlab_last_synced_at: string | null;
  has_passphrase: boolean;
  webtool_zip_present: boolean;
  commit_count_tp: number | null;
  commit_count_projet: number | null;
  commit_stats_synced_at: string | null;
}

interface ActionResponse {
  message: string;
  [key: string]: any;
}

export interface GenerateProjectGroupsResult {
  message: string;
  created: string[];
  skipped: string[];
}

async function callApi(path: string, method: 'GET' | 'POST', accessToken: string, body?: any): Promise<any> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json.message || `HTTP error! status: ${response.status}`);
  }

  return json;
}

class GitlabAdminService {

  async listGroups(accessToken: string): Promise<GitlabGroupStatus[]> {
    const json = await callApi('/admin/gitlab/groups', 'GET', accessToken);
    return json.data;
  }

  async syncGroup(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/sync`, 'POST', accessToken);
  }

  async createRepo(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/create-repo`, 'POST', accessToken);
  }

  async regenerateDeployKey(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/deploy-key`, 'POST', accessToken);
  }

  async lockdownGroup(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/lockdown`, 'POST', accessToken);
  }

  async unlockGroup(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/unlock`, 'POST', accessToken);
  }

  async createAllRepos(accessToken: string): Promise<ActionResponse> {
    return callApi('/admin/gitlab/groups/create-all-repos', 'POST', accessToken);
  }

  async lockdownAll(accessToken: string): Promise<ActionResponse> {
    return callApi('/admin/gitlab/groups/lockdown-all', 'POST', accessToken);
  }

  async syncAllGroups(accessToken: string): Promise<ActionResponse> {
    return callApi('/admin/gitlab/groups/sync-all', 'POST', accessToken);
  }

  async generateWebtoolPackage(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/webtool-package`, 'POST', accessToken);
  }

  async syncCommitStats(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/commit-stats/sync`, 'POST', accessToken);
  }

  async syncAllCommitStats(accessToken: string): Promise<ActionResponse> {
    return callApi('/admin/gitlab/groups/commit-stats/sync-all', 'POST', accessToken);
  }

  async rotateAllCredentials(accessToken: string): Promise<ActionResponse> {
    return callApi('/admin/gitlab/groups/credentials-rotate-all', 'POST', accessToken);
  }

  async archiveAndReset(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/gitlab/groups/${groupId}/archive-and-reset`, 'POST', accessToken);
  }

  // Efface les données de test d'un groupe : résultats CI, rapports zip, stats de commits
  async resetResults(groupId: number, accessToken: string): Promise<ActionResponse> {
    return callApi(`/admin/project-groups/${groupId}/reset-results`, 'POST', accessToken);
  }

  async archiveAndResetAll(accessToken: string): Promise<ActionResponse> {
    return callApi('/admin/gitlab/groups/archive-and-reset-all', 'POST', accessToken);
  }

  // Crée les emplacements de groupe projet vides (nombre de binômes par TD, variable chaque
  // année) — voir ProjectGroupGenerateResource. Idempotent : les emplacements déjà créés sont
  // simplement ignorés, on peut donc relancer avec un total plus grand pour n'en ajouter que
  // le complément.
  async generateProjectGroups(tdGroup: string, count: number, accessToken: string): Promise<GenerateProjectGroupsResult> {
    return callApi('/admin/project-groups/generate', 'POST', accessToken, { td_group: tdGroup, count });
  }

  // Crée les bacs à sable 666 et 667 de chaque groupe TD actif (projets de test des chargé·es de
  // TP) — voir ProjectGroupSandboxGenerateResource. Idempotent.
  async generateSandboxGroups(accessToken: string): Promise<GenerateProjectGroupsResult> {
    return callApi('/admin/project-groups/sandboxes', 'POST', accessToken);
  }

  // Télécharge le zip webtool d'un groupe (même route que la page de résultats étudiante)
  async downloadWebtoolZip(groupId: number, groupName: string, accessToken: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/project-groups/${groupId}/webtool-zip`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
    });

    if (!response.ok) {
      const json = await response.json().catch(() => ({}));
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `webtool_${groupName}.zip`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const gitlabAdminService = new GitlabAdminService();
